import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { RefreshCw, Trash2 } from 'lucide-react-native';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, Animated } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { ActiveFeatureFlags } from '@/components/account/ActiveFeatureFlags';
import { DisclosureSection } from '@/components/account/DisclosureSection';
import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { useChatTransportMode } from '@/hooks/chat/useChatTransportMode';
import { useAccountSwitcher } from '@/hooks/useAccountSwitcher';
import { useAnalyticsSettings } from '@/hooks/useAnalyticsSettings';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useColors } from '@/hooks/useColors';
import { useToaster } from '@/hooks/useToaster';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getNativeTextProps } from '@/util/getNativeTextProps';

const SCREEN_COLUMN = getScreenColumnClasses({ maxWidth: 'max-w-screen-md' });

interface StorageItem {
  key: string;
  value: string;
}

/**
 * Fetches and returns storage items from either localStorage (web) or AsyncStorage (native).
 * It specifically filters for keys prefixed with "primum:" to only show app-relevant data.
 *
 * @returns A promise that resolves to an array of storage items, each with a key and value.
 */
async function getStorageData(): Promise<StorageItem[]> {
  if (isWeb) {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.log('localStorage not available');

      return [];
    }

    const keys = Object.keys(window.localStorage);

    const filteredKeys = keys.filter((key) => key.startsWith('primum:'));

    const items = filteredKeys.map((key) => ({
      key,
      value: window.localStorage.getItem(key) ?? '',
    }));

    return items;
  }

  const keys = await AsyncStorage.getAllKeys();

  const filteredKeys = keys.filter((key) => key.startsWith('primum:'));

  const items = await AsyncStorage.multiGet(filteredKeys);

  return items.map(([key, value]) => ({
    key,
    value: value ?? '',
  }));
}

/**
 * Developer settings
 *
 * A debug and utility screen for developers and internal users (dev/primum Cognito groups).
 * Provides access to view and manage local storage and toggle Segment analytics tracking. The
 * announcement banner switch lives on the account page's Primum section.
 *
 * Features:
 * - Displays the signed-in person's Cognito groups, and the account acted as while acting.
 * - Shows which chat transport the session runs.
 * - Toggles Segment analytics tracking (for dev users).
 * - Lists every feature flag that is on.
 * - Displays all "primum:" prefixed items from local/AsyncStorage.
 * - Allows reloading and deleting individual storage items.
 * - Provides toast notifications and animations for storage operations.
 *
 * Who may reach this screen is decided by the secure group's layout, which drops the route
 * from the navigator for anyone outside the primum group rather than letting them arrive and
 * redirecting them off it. It used to be enforced here, and the guard reproduces its
 * loading-window behavior exactly: membership reads as unknown until the Cognito groups
 * query settles, and the route stays open until then.
 */
export default function PrimumSettings() {
  const { isDevUser, groups } = useCognitoGroups();

  const {
    data: storageData,
    refetch,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['toolsStorage'],
    queryFn: getStorageData,
    staleTime: 0,
  });

  const [storageQuery, setStorageQuery] = useState('');

  const visibleStorage = useMemo(() => {
    const needle = storageQuery.trim().toLowerCase();

    return (storageData ?? []).filter(
      (item) =>
        !needle ||
        item.key.toLowerCase().includes(needle) ||
        item.value.toLowerCase().includes(needle)
    );
  }, [storageData, storageQuery]);

  const { settings, setSegmentEnabled } = useAnalyticsSettings();

  const { colors } = useColors();

  const toaster = useToaster();

  const { bottom } = useSafeAreaInsets();

  const rotateAnim = useRef(new Animated.Value(0)).current;

  const handleRefresh = useCallback(async () => {
    rotateAnim.setValue(0);

    Animated.timing(rotateAnim, {
      toValue: 1,
      duration: 600,
      useNativeDriver: true,
    }).start();

    try {
      await refetch();

      toaster.showSuccess({
        title: `Reloaded storage (${storageData?.length || 0} items)`,
      });
    } catch {
      toaster.showError({
        title: 'Unable to reload storage data',
      });
    }
  }, [refetch, rotateAnim, toaster, storageData?.length]);

  const handleRemoveKey = async (key: string) => {
    try {
      if (isWeb) {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.removeItem(key);
        }
      } else {
        await AsyncStorage.removeItem(key);
      }

      await refetch();

      toaster.showSuccess({
        title: `Deleted: ${key}`,
      });
    } catch {
      toaster.showError({
        title: 'Unable to remove storage item',
      });
    }
  };

  const renderStorageItem = ({ item }: { item: StorageItem }) => (
    <VStack className="gap-1 py-3">
      <HStack className="items-center justify-between gap-2">
        <Text size="sm" className="flex-1 break-all font-medium font-mono text-typography-950">
          {item.key}
        </Text>
        <Pressable
          onPress={() => handleRemoveKey(item.key)}
          {...getAccessibilityProps({ role: 'button', label: `Delete ${item.key}` })}
          className="flex items-center justify-center p-2">
          <Icon as={Trash2} size="sm" className="text-primary-600" />
        </Pressable>
      </HStack>
      <Text
        size="sm"
        className="break-all text-typography-700 web:line-clamp-2"
        {...getNativeTextProps({ numberOfLines: 2 })}>
        {item.value}
      </Text>
    </VStack>
  );

  const getStorageContent = () => {
    if (isLoading) {
      return (
        <VStack className="flex-1 items-center justify-center py-8">
          <Text className="text-typography-700">Loading storage...</Text>
        </VStack>
      );
    }

    if (error) {
      return (
        <VStack className="flex-1 items-center justify-center py-8">
          <Text className="text-typography-700">Error loading storage</Text>
          <Text className="mt-2 text-sm text-typography-600">{String(error)}</Text>
        </VStack>
      );
    }

    if (visibleStorage.length > 0) {
      return (
        <VStack>
          {visibleStorage.map((item, index) => (
            <VStack key={item.key}>
              {index > 0 ? <Divider /> : null}

              {renderStorageItem({ item })}
            </VStack>
          ))}
        </VStack>
      );
    }

    return (
      <Text size="sm" className="text-typography-700">
        {storageQuery.trim() ? 'No storage items match.' : 'No storage items.'}
      </Text>
    );
  };

  const storageContent = getStorageContent();

  return (
    <VStack
      className={twJoin('flex-1', SCREEN_COLUMN.frame)}
      style={{
        paddingBottom: bottom,
      }}>
      <Header
        contentScrollsUnderHeader
        title="Developer settings"
        statusBarBackgroundColor={colors.background[0]}
        backRoute={ROUTES.ACCOUNT}
        isBackButton
      />

      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className="flex-1"
        contentContainerClassName={SCREEN_COLUMN.content}
        showsVerticalScrollIndicator>
        <VStack className="py-6" space="lg">
          <SessionIdentity groups={groups} />

          <Divider />

          <ChatTransportRow />

          {isDevUser && (
            <>
              <Divider />

              <VStack space="md">
                <HStack className="w-full items-start justify-between gap-4">
                  <VStack className="flex-shrink">
                    <Text className="font-semibold text-typography-950">Analytics Tracking</Text>
                    <Text size="xs" className="text-typography-900">
                      Enable or disable Segment events.
                    </Text>
                  </VStack>
                  <Switch
                    size="sm"
                    trackColor={{ true: colors.secondary[500], false: colors.background[200] }}
                    value={settings?.isSegmentEnabled ?? true}
                    onValueChange={async (enabled) => {
                      await setSegmentEnabled(enabled);

                      refetch();
                    }}
                  />
                </HStack>
              </VStack>
            </>
          )}

          <Divider />

          <ActiveFeatureFlags />

          {isDevUser && (
            <>
              <Divider />

              <DisclosureSection
                title={`Storage (${storageData?.length ?? 0})`}
                accessibilityLabel={`Storage, ${storageData?.length ?? 0} items`}>
                <HStack className="items-center justify-between gap-2">
                  <Text size="xs" className="flex-1 text-typography-900">
                    View or delete app storage. Some require reload on mobile.
                  </Text>
                  <Pressable
                    onPress={handleRefresh}
                    className="p-2"
                    disabled={isLoading}
                    {...getAccessibilityProps({ role: 'button', label: 'Reload storage' })}>
                    <Animated.View
                      style={{
                        transform: [
                          {
                            rotate: rotateAnim.interpolate({
                              inputRange: [0, 1],
                              outputRange: ['0deg', '360deg'],
                            }),
                          },
                        ],
                      }}>
                      <Icon as={RefreshCw} size="sm" className="text-typography-600" />
                    </Animated.View>
                  </Pressable>
                </HStack>

                <Input size="sm">
                  <InputField
                    placeholder="Search storage"
                    value={storageQuery}
                    onChangeText={setStorageQuery}
                    autoCapitalize="none"
                    autoCorrect={false}
                    accessibilityLabel="Search storage"
                  />
                </Input>

                {storageContent}
              </DisclosureSection>
            </>
          )}
        </VStack>
      </ScrollView>
    </VStack>
  );
}

/** Which transport the chat session runs, as the `chat_transport` toggle resolves it. */
function ChatTransportRow() {
  const mode = useChatTransportMode();

  return (
    <HStack className="items-baseline gap-2">
      <Text className="font-semibold text-typography-950">Chat transport:</Text>
      <Text size="sm" className="font-mono text-typography-900">
        {mode}
      </Text>
    </HStack>
  );
}

/**
 * Who this session is. The Cognito groups are the signed-in person's own, read from their token,
 * and decide who sees the staff tools; they do not change while acting as another account. So
 * while acting, the account acted as and its user type show beside them, since that account is
 * the one the inbox, chat and server permissions run as.
 */
function SessionIdentity({ groups }: { groups?: string[] }) {
  const { actAsUserId, currentAccount } = useAccountSwitcher();

  const userId = useAtomValue(userIdAtom);

  const { data: actingUser } = useQuery({
    ...getCurrentUserV2QueryOptions(userId),
    enabled: Boolean(actAsUserId && userId),
  });

  const actingLabel = currentAccount?.label ?? 'Another account';

  return (
    <VStack space="sm">
      <HStack className="items-baseline gap-2">
        <Text className="font-semibold text-typography-950">Cognito groups:</Text>
        <Text size="sm" className="text-typography-900">
          {groups?.join(', ') || 'Loading...'}
        </Text>
      </HStack>

      {actAsUserId ? (
        <HStack className="items-baseline gap-2">
          <Text className="font-semibold text-typography-950">Acting as:</Text>
          <Text size="sm" className="text-typography-900">
            {actingUser?.userType ? `${actingLabel} (${actingUser.userType})` : actingLabel}
          </Text>
        </HStack>
      ) : null}
    </VStack>
  );
}
